<script>
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useDebounceFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { Spinner, Switch, Icon, NextButton },
  props: {
    selectedKnowledgebase: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const rules = {
      url: { required },
    };

    const { t } = useI18n();

    const store = useStore();
    const url = ref('');
    const images = ref([]);
    const selectedImages = ref([]);
    const previousUrl = ref('');
    const uiFlags = computed(() => store.getters['media/getUIFlags']);
    const pageTitle = computed(() => t('LIBRARIES.MEDIA.TITLE'));

    const v$ = useVuelidate(rules, { url });

    const fetchImages = useDebounceFn(async () => {
      if (url.value && url.value !== previousUrl.value) {
        previousUrl.value = url.value;
        try {
          const data = await store.dispatch('media/fetchImages', {
            knowledgebaseId: props.selectedKnowledgebase.id,
            url: url.value,
          });
          const fetchedImages = data.images || [];
          images.value = fetchedImages;
          selectedImages.value = [];
        } catch (error) {
          useAlert(t('LIBRARIES.MEDIA.API.ERROR_MESSAGE'));
        }
      }
    }, 1000);

    watch(url, fetchImages, { immediate: true });

    const onClose = () => {
      emit('on-close');
    };

    const addMedia = async () => {
      if (selectedImages.value.length === 0) {
        useAlert(t('LIBRARIES.MEDIA.NO_IMAGES_SELECTED'));
        return;
      }
      try {
        await store.dispatch('media/createRecords', {
          knowledgebaseId: props.selectedKnowledgebase.id,
          images: selectedImages.value,
        });
        useAlert(t('LIBRARIES.MEDIA.API.SUCCESS_MESSAGE'));
        onClose();
      } catch (error) {
        useAlert(t('LIBRARIES.MEDIA.API.ERROR_MESSAGE'));
      }
    };

    const toggleSelectAll = () => {
      if (selectedImages.value.length === images.value.length) {
        selectedImages.value = [];
      } else {
        selectedImages.value = images.value.map(image => image);
      }
    };

    return {
      v$,
      url,
      images,
      selectedImages,
      uiFlags,
      pageTitle,
      onClose,
      addMedia,
      toggleSelectAll,
    };
  },
  mounted() {
    this.$nextTick(() => {
      const el = this.$refs.firstInput?.$el?.querySelector?.('input');
      if (el) el.focus();
    });
  },
};
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col flex-1 max-w-6xl">
    <woot-modal-header :header-title="pageTitle" />

    <div class="flex flex-col w-full mt-2 px-8">
      <p>
        {{ $t('LIBRARIES.MEDIA.ADD.DESCRIPTION') }}
      </p>
    </div>

    <form
      class="flex flex-col w-full"
      @submit.prevent="addMedia"
      @keydown.enter.prevent="addMedia"
    >
      <div class="w-full">
        <woot-input
          ref="firstInput"
          v-model="url"
          :label="$t('LIBRARIES.MEDIA.ADD.FORM.URL.LABEL')"
          type="text"
          class="mb-0"
          :class="{ error: v$.url.$error }"
          :error="v$.url.$error ? $t('LIBRARIES.MEDIA.ADD.FORM.URL.ERROR') : ''"
          :placeholder="$t('LIBRARIES.MEDIA.ADD.FORM.URL.PLACEHOLDER')"
          @blur="v$.url.$touch"
        />
      </div>

      <div v-if="uiFlags.isFetchingUrls">
        <Spinner :size="48" />
      </div>

      <div v-else-if="images.length > 0" class="w-full my-4">
        <div class="flex items-center mb-2">
          <input
            id="select-all"
            type="checkbox"
            :checked="selectedImages.length === images.length"
            @change="toggleSelectAll"
          />
          <label for="select-all" class="ml-2">Select All</label>
        </div>
        <div class="grid grid-cols-4 gap-4 max-h-[60dvh] overflow-y-auto">
          <div v-for="image in images" :key="image.src" class="relative">
            <input
              :id="'image-' + image.src"
              v-model="selectedImages"
              type="checkbox"
              :value="image"
              class="absolute top-2 left-2 z-10"
            />
            <label :for="'image-' + image.src" class="block w-full h-32">
              <img
                :src="image.src"
                :alt="image.alt || 'Image preview'"
                class="w-full h-32 object-cover rounded-md cursor-pointer"
                loading="lazy"
              />
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <NextButton
          md
          ghost
          slate
          :label="$t('LIBRARIES.MEDIA.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :is-loading="uiFlags.isCreating"
          :label="$t('LIBRARIES.MEDIA.CREATE_BUTTON_TEXT')"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
::v-deep .ProseMirror-woot-style {
  height: 5rem;
}
</style>
